// module imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// file imports
import axiosInstance from '@/lib/axios';

interface GetLeadsArgs {
  params?: Record<string, string | number>;
}

const getLeads = createAsyncThunk('leads/getLeads', async ({ params = {} }: GetLeadsArgs, { rejectWithValue }) => {
  try {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    const response = await axiosInstance.get(`/leads${query ? `?${query}` : ''}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving leads failed');
  }
});

const getLead = createAsyncThunk('lead/getLead', async (id: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/leads/${id}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving single lead failed');
  }
});

const updateLead = createAsyncThunk('lead/updateLead', async (params: { id: string; updatedData: any }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.patch(`/leads/${params.id}`, params.updatedData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Updating lead failed');
  }
});

const deleteLead = createAsyncThunk('lead/deleteLead', async (id: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete(`/leads/${id}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Deleting lead failed');
  }
});

const leadSlice = createSlice({
  name: 'lead',
  initialState: {
    data: null as any,
    loading: false,
    error: null as string | null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // getLeads
      .addCase(getLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // getLead
      .addCase(getLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLead.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updateLead
      .addCase(updateLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(updateLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // deleteLead
      .addCase(deleteLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(deleteLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = leadSlice.actions;
export { deleteLead, getLead, getLeads, updateLead };
export default leadSlice.reducer;
